import React, { useState } from 'react';
import { AvTooltip } from '@fabric-ui/components';
import { outlinedInputClasses, TextField } from '@mui/material';
import { cn } from '@utils/cn';
import { editFieldStyle } from '../../Tickets/ticketInfo.utils';

interface WidgetTitleInlineEditProps {
  title: string;
  onChange: (title: string) => void;
  className?: string;
  placeholder?: string;
  editWhenEmpty?: boolean;
}

const WidgetTitleInlineEdit: React.FC<WidgetTitleInlineEditProps> = ({
  title,
  onChange,
  className,
  placeholder = '',
  editWhenEmpty = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const showTextField = isEditing || (editWhenEmpty && !title);

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const { value } = e.currentTarget;
    if (value !== title) {
      onChange(value);
    }
    setIsEditing(false);
  };

  const handleTitleClick = () => setIsEditing(true);

  const handleTextFieldKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      (e.target as HTMLTextAreaElement).blur();
    }
  };

  return (
    <div
      className={cn(
        'draggableCancel -mx-3 w-full min-w-0 flex-1 rounded-lg px-3 py-0.5 transition-colors',
        (title || editWhenEmpty) && 'cursor-pointer hover:bg-[var(--color-surface-list-hover)]',
        className
      )}>
      {showTextField ? (
        <TextField
          autoFocus
          sx={{
            ...editFieldStyle,
            width: '100%',
            maxWidth: 'none',
            [`.${outlinedInputClasses.notchedOutline}`]: { border: 'none', padding: 0, display: 'none' },
          }}
          InputProps={{
            sx: t => ({
              ...t.typography.h5,
              minHeight: 'auto',
              lineHeight: '1.5',
              '&.MuiInputBase-multiline': { padding: 0 },
            }),
            className: 'transparent',
          }}
          defaultValue={title}
          onBlur={handleBlur}
          onKeyDown={handleTextFieldKeyDown}
          placeholder={placeholder}
          fullWidth
          multiline
          maxRows={2}
        />
      ) : (
        <AvTooltip {...{ component: 'h5' }} sx={{ fontSize: '18px', fontWeight: 600, ...(!title && { opacity: 0.5 }) }}>
          <button type="button" onClick={handleTitleClick} className="line-clamp-2 w-full whitespace-normal break-words text-left">
            {title}
          </button>
        </AvTooltip>
      )}
    </div>
  );
};

export default WidgetTitleInlineEdit;
